import { useState, useEffect, useCallback, useRef } from "react";
import { AlertTriangle, Trash2, X } from "lucide-react";

/**
 * Reusable destructive confirmation modal — the styled replacement for
 * `window.confirm(...)`. Matches the visual language of the extracted
 * `<DeleteBookingDialog>` (iter-22) so every ops confirmation looks
 * identical.
 *
 * `useConfirm()` returns { confirm, dialog } — call `await confirm({...})`
 * to show the dialog and receive `true` / `false`, then drop `{dialog}`
 * anywhere in your JSX (once per page). The hook memoises props between
 * confirms and clears state on unmount.
 */
export default function ConfirmDialog({
  open, title, message, subject, subtitle,
  confirmLabel = "Delete", cancelLabel = "Cancel",
  tone = "danger",   // "danger" | "primary"
  busy = false,
  onCancel, onConfirm,
  testId = "confirm-dialog",
}) {
  const confirmRef = useRef(null);

  // Focus the primary action on open for keyboard-first users.
  useEffect(() => {
    if (open && confirmRef.current) {
      const t = setTimeout(() => confirmRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [open]);

  // ESC = cancel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onCancel?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const isDanger = tone === "danger";
  const HeaderIcon = isDanger ? Trash2 : AlertTriangle;
  const confirmClasses = isDanger
    ? "bg-rose-600 text-white hover:opacity-90"
    : "bg-black text-white dark:bg-white dark:text-black hover:opacity-90";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onCancel}
      data-testid={testId}
    >
      <div
        className="bg-white dark:bg-[#1F2937] max-w-md w-full p-6 rounded border border-black/10 dark:border-white/10"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between mb-2">
          <h3 className={`font-display text-xl flex items-center gap-2 ${isDanger ? "text-rose-600 dark:text-rose-400" : "text-neutral-900 dark:text-neutral-100"}`}>
            <HeaderIcon size={18} /> {title}
          </h3>
          <button onClick={onCancel} aria-label="Close" className="text-neutral-400 hover:text-black dark:hover:text-white">
            <X size={16} />
          </button>
        </div>
        {message && (
          <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-1">{message}</p>
        )}
        {subject && (
          <div className="bg-black/[0.03] dark:bg-white/[0.03] rounded p-3 my-3 text-xs">
            <code className="text-neutral-700 dark:text-neutral-300 break-all">{subject}</code>
          </div>
        )}
        {subtitle && (
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4 italic">{subtitle}</p>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="border border-black/15 dark:border-white/15 px-4 py-1.5 text-sm rounded hover:border-black dark:hover:border-white disabled:opacity-50"
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`${confirmClasses} px-4 py-1.5 text-sm rounded inline-flex items-center gap-1 disabled:opacity-50`}
            data-testid={`${testId}-confirm`}
          >
            {isDanger && <Trash2 size={12} />}
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}


/**
 * Hook that turns the `window.confirm()` call pattern into a promise —
 * lets callers keep their existing "if not confirmed → return" flow
 * without rewriting async chains:
 *
 *   const { confirm, dialog } = useConfirm();
 *   ...
 *   const ok = await confirm({ title:"Delete voucher", message:"...", subject:code });
 *   if (!ok) return;
 *   // ... proceed with the destructive action
 *
 *   // render once per page:
 *   { dialog }
 */
export function useConfirm() {
  const [state, setState] = useState({ open: false });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, ...opts });
    });
  }, []);

  const handleCancel = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    resolverRef.current?.(false);
    resolverRef.current = null;
  }, []);

  const handleConfirm = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
    resolverRef.current?.(true);
    resolverRef.current = null;
  }, []);

  const dialog = (
    <ConfirmDialog
      {...state}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    />
  );

  return { confirm, dialog };
}
